import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Box,
  Avatar,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Alert,
} from '@mui/material';
import { ReportProblem as ReportProblemIcon } from '@mui/icons-material';
import { getInvitationDetails, acceptInvitation, declineInvitation } from '../services/api';

interface InvitationDetails {
  organization_name: string;
  organization_logo_url: string | null;
  inviter_name: string;
  inviter_email: string;
  invitee_email: string;
  message: string | null;
  status: string;
}

const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const token = searchParams.get('token');

  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    if (token) {
      const fetchDetails = async () => {
        try {
          const response = await getInvitationDetails(token);
          setDetails(response);
          setError(null);
        } catch (err: any) {
          const errorMessage = err.response?.data?.message || 'Failed to fetch invitation details. The link may be invalid or expired.';
          setError(errorMessage);
          setDetails(null);
        } finally {
          setLoading(false);
        }
      };
      fetchDetails();
    } else {
      setError('No invitation token provided.');
      setLoading(false);
    }
  }, [token]);

  const handleAccept = async () => {
    if (token) {
      try {
        const response = await acceptInvitation(token);

        const emailMismatch = isAuthenticated && user && details && user.email !== details.invitee_email;

        if (emailMismatch) {
          logout(false);
        }

        if (!isAuthenticated || emailMismatch) {
          if (response.reset_token) {
            navigate(`/reset-password?token=${response.reset_token}&invitation=true`);
          } else {
            navigate('/login');
          }
        } else {
          navigate('/select-organization?refresh=true');
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to accept invitation.';
        setError(errorMessage);
        setDetails(null);
      }
    }
  };

  const handleDecline = async () => {
    if (token) {
      try {
        await declineInvitation(token, declineReason);
        setOpenDeclineDialog(false);
        // You might want to redirect to a page confirming the decline
        navigate('/login');
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to decline invitation.';
        setError(errorMessage);
        setDetails(null);
        setOpenDeclineDialog(false);
      }
    }
  };

  const handleOpenDeclineDialog = () => {
    setOpenDeclineDialog(true);
  };

  const handleCloseDeclineDialog = () => {
    setOpenDeclineDialog(false);
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="sm">
        <Card sx={{
          borderRadius: 2,
          boxShadow: 3,
          p: 4,
          textAlign: 'center'
        }}>
          <CardContent>
            {details ? (
              <>
                <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                  {details.organization_logo_url ? (
                    <Avatar src={details.organization_logo_url} alt={details.organization_name} sx={{ width: 80, height: 80, mb: 2 }} />
                  ) : (
                    <Avatar sx={{ width: 80, height: 80, mb: 2, bgcolor: 'primary.main', fontSize: '2.5rem' }}>
                      {details.organization_name.charAt(0)}
                    </Avatar>
                  )}
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
                    {details.organization_name}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    You're invited to join
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Invited by: <strong>{details.inviter_name}</strong> ({details.inviter_email})
                </Typography>
                {details.message && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 4 }}>
                    "{details.message}"
                  </Typography>
                )}
                {details.status.toLowerCase() === 'pending' ? (
                  <Box mt={4} display="flex" justifyContent="center" gap={2}>
                    <Button variant="contained" color="primary" onClick={handleAccept} size="large">
                      Accept Invitation
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={handleOpenDeclineDialog} size="large">
                      Decline
                    </Button>
                  </Box>
                ) : details.status.toLowerCase() === 'accepted' ? (
                  <Alert severity="success" sx={{ mt: 4 }}>
                    You have already accepted this invitation.
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ mt: 4 }}>
                    This invitation is no longer valid. It has been {details.status}.
                  </Alert>
                )}
              </>
            ) : (
              <Box>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
                  Invitation
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      <Dialog
        open={openDeclineDialog}
        onClose={handleCloseDeclineDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportProblemIcon color="warning" />
            Decline Invitation
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography>
              Are you sure you want to decline this invitation? You can optionally provide a reason below.
            </Typography>
            <TextField
              autoFocus
              id="reason"
              label="Reason for declining (optional)"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeclineDialog}>Cancel</Button>
          <Button onClick={handleDecline} variant="contained" color="secondary">Decline</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AcceptInvitation;